
import { Link } from 'react-router-dom';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Badge } from '@/components/ui/badge';
import { format } from 'date-fns';
import { parseLocalDate } from '@/lib/dateUtils';
import { Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { usePets } from '../hooks/usePets';
import { useAuth } from '../contexts/AuthContext';
import FollowButton from './FollowButton';
import { translateSpeciesSingular } from '@/utils/speciesTranslation';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";

interface PetListItemProps {
  pet: {
    id: string;
    name: string;
    species?: string;
    breed?: string;
    photo_url?: string;
    birth_date?: string;
    is_memorial: boolean;
    user_id?: string;
  };
}

const PetListItem = ({ pet }: PetListItemProps) => {
  const { t } = useTranslation();
  const { user: currentUser } = useAuth();
  const [isDeleting, setIsDeleting] = useState(false);
  const { deletePet } = usePets();
  
  // Check if current user owns this pet
  const isOwner = currentUser?.id === pet.user_id;
  
  // Format birth date if available
  const formattedBirthDate = pet.birth_date 
    ? format(parseLocalDate(pet.birth_date), 'MMM d, yyyy')
    : null;

  const handleDelete = async () => {
    try {
      setIsDeleting(true);
      console.log('🗑️ Attempting to delete pet:', pet.id);
      
      await new Promise((resolve, reject) => {
        deletePet(pet.id, {
          onSuccess: () => {
            console.log('🗑️ Pet deleted successfully:', pet.id);
            resolve(true);
          },
          onError: (error) => {
            console.error('🗑️ Error deleting pet:', error);
            reject(error);
          }
        });
      });
    } catch (error) {
      console.error('🗑️ Delete operation failed:', error);
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className={`flex items-center p-3 rounded-lg border transition-colors relative ${pet.is_memorial ? 'memorial-pet-border' : 'border-gray-200 bg-white hover:bg-gray-50'}`}>
      {/* Main clickable area for navigation */}
      <Link 
        to={`/pet/${pet.id}`} 
        className="flex items-center flex-1"
      >
        <div className="relative">
          <Avatar className={`h-16 w-16 ${pet.is_memorial ? 'opacity-80' : ''}`}>
            {pet.photo_url ? (
              <AvatarImage src={pet.photo_url} alt={pet.name} />
            ) : (
              <AvatarFallback className="bg-pet-purple/20 text-pet-purple">
                {pet.name.charAt(0).toUpperCase()}
              </AvatarFallback>
            )}
          </Avatar>
          {pet.is_memorial && (
            <div className="absolute -top-1 -right-1 w-5 h-5 bg-gray-400 rounded-full flex items-center justify-center">
              <span className="text-white text-xs">✝</span>
            </div>
          )}
        </div>
        
        <div className="ml-4 flex-1">
          <div className="flex items-center">
            <h3 className={`font-medium ${pet.is_memorial ? 'text-gray-600' : ''}`}>{pet.name}</h3>
            {pet.is_memorial && (
              <Badge variant="outline" className="ml-2 text-gray-500 border-gray-300">
                {t('modals.addPet.memorial')}
              </Badge>
            )}
          </div>
          
          {(pet.species || pet.breed) && (
            <p className="text-sm text-gray-500">
              {[translateSpeciesSingular(pet.species, t), pet.breed].filter(Boolean).join(" • ")}
            </p>
          )}

          {formattedBirthDate && (
            <p className="text-xs text-gray-400 mt-1">
              Born: {formattedBirthDate}
            </p>
          )}
        </div>
      </Link>
      
      {/* Action buttons outside the Link */}
      <div className="flex items-center gap-2">
        {/* Follow button for pet owner - only show if not the owner */}
        {!isOwner && pet.user_id && (
          <FollowButton 
            targetUserId={pet.user_id} 
            size="sm"
          />
        )}
        
        {/* Delete button - only show for owner */}
        {isOwner && (
          <AlertDialog>
            <AlertDialogTrigger asChild>
              <Button 
                variant="ghost" 
                size="icon"
                className="h-8 w-8 rounded-full hover:bg-red-50 hover:text-red-600"
                disabled={isDeleting}
              >
                <Trash2 className="h-4 w-4" />
              </Button>
            </AlertDialogTrigger>
            <AlertDialogContent>
              <AlertDialogHeader>
                <AlertDialogTitle>{t('modals.deleteConfirm.deletePet', { petName: pet.name })}</AlertDialogTitle>
                <AlertDialogDescription className="text-left space-y-2">
                  <p><strong>{t('modals.deleteConfirm.deletePetWarning')}</strong></p>
                  <p>{t('modals.deleteConfirm.deletePetDescription', { petName: pet.name })}:</p>
                  <ul className="list-disc list-inside ml-4 space-y-1">
                    <li>{t('modals.deleteConfirm.petInfo', { petName: pet.name })}</li>
                    <li>{t('modals.deleteConfirm.petPhotos')}</li>
                    <li>{t('modals.deleteConfirm.petPosts')}</li>
                    <li>{t('modals.deleteConfirm.medicalRecords')}</li>
                    <li>{t('modals.deleteConfirm.commentsLikes')}</li>
                  </ul>
                  <p className="text-red-600 font-medium">{t('modals.deleteConfirm.cannotRecover')}</p>
                </AlertDialogDescription>
              </AlertDialogHeader>
              <AlertDialogFooter>
                <AlertDialogCancel disabled={isDeleting}>
                  {t('modals.deleteConfirm.cancel')}
                </AlertDialogCancel>
                <AlertDialogAction
                  onClick={handleDelete}
                  className="bg-red-500 hover:bg-red-600 text-white"
                  disabled={isDeleting}
                >
                  {isDeleting ? t('modals.deleteConfirm.deleting') : t('modals.deleteConfirm.deletePetButton')}
                </AlertDialogAction>
              </AlertDialogFooter>
            </AlertDialogContent>
          </AlertDialog>
        )}
      </div>
    </div>
  );
};

export default PetListItem;
